/**
 * Two-Set Bundle Builder
 * ----------------------
 * Architecture: BundleUtils -> SectionConfetti -> BundleState -> BundleStorage
 *               -> BundleCart -> BundleRenderer -> BundleSetBuilder (controller)
 *
 * This is a pure internal refactor of the original single-class implementation.
 * Customer-facing behaviour, markup, CSS classes, data attributes, cart payload,
 * and storage format are all unchanged. Only the internal structure differs.
 */
(() => {
  const ROOT_SELECTOR = '[data-set-builder]';

  /**
   * BundleUtils
   * Stateless, DOM-free helper functions shared across the builder.
   */
  const BundleUtils = {
    /** Lowercases, strips accents/punctuation, and collapses whitespace for loose matching. */
    normalise(value) {
      return String(value || '')
        .normalize('NFKD')
        .toLowerCase()
        .replace(/&/g, ' and ')
        .replace(/[^a-z0-9]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    },

    /** Formats a minor-unit price (e.g. paise) using the theme's money_format string. */
    formatMoney(cents, moneyFormat) {
      const value = Number.isFinite(Number(cents)) ? Number(cents) : 0;
      const amount = (value / 100).toFixed(2);
      const formatted = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      const noDecimals = Math.round(value / 100).toLocaleString('en-IN');

      return (moneyFormat || '₹{{amount}}')
        .replace('{{amount}}', formatted)
        .replace('{{ amount }}', formatted)
        .replace('{{amount_no_decimals}}', noDecimals)
        .replace('{{ amount_no_decimals }}', noDecimals);
    },

    /** Generates a unique id for a single "add both sets" cart transaction. */
    createBundleId() {
      if (window.crypto?.randomUUID) return window.crypto.randomUUID();
      return `bundle-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    },

    /** Escapes a value for safe interpolation into innerHTML. */
    escapeHtml(value) {
      return String(value || '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;');
    },
  };

  /**
   * SectionConfetti
   * Canvas confetti burst used to celebrate completing a set. Unchanged from
   * the original implementation aside from added documentation.
   */
  class SectionConfetti {
    constructor(canvas) {
      this.canvas = canvas || null;
      this.ctx = this.canvas ? this.canvas.getContext('2d') : null;
      this.particles = [];
      this.rafId = null;
      this.colors = ['#9b4d2d', '#f5e7de', '#246b45', '#e8c1a0', '#437b80', '#ffffff'];

      if (this.canvas) {
        this.resize = this.resize.bind(this);
        this.resize();
        window.addEventListener('resize', this.resize);
      }
    }

    /** Resizes the canvas backing store to match its parent, respecting device pixel ratio. */
    resize() {
      if (!this.canvas) return;
      const parent = this.canvas.parentElement;
      if (!parent) return;

      const rect = parent.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;

      this.canvas.width = Math.max(1, Math.round(rect.width * dpr));
      this.canvas.height = Math.max(1, Math.round(rect.height * dpr));
      this.canvas.style.width = `${rect.width}px`;
      this.canvas.style.height = `${rect.height}px`;
      this.ctx?.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    /** Spawns a burst of confetti particles, unless the user prefers reduced motion. */
    burst(options = {}) {
      if (!this.ctx || !this.canvas) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      const count = options.count || 140;
      const width = this.canvas.clientWidth || this.canvas.parentElement?.clientWidth || 0;
      if (!width) return;

      for (let i = 0; i < count; i += 1) {
        this.particles.push({
          x: Math.random() * width,
          y: -20 - Math.random() * 160,
          size: 5 + Math.random() * 7,
          color: this.colors[Math.floor(Math.random() * this.colors.length)],
          speedY: 2.2 + Math.random() * 3,
          speedX: (Math.random() - 0.5) * 2.4,
          rotation: Math.random() * 360,
          rotationSpeed: (Math.random() - 0.5) * 14,
          shape: Math.random() > 0.5 ? 'rect' : 'circle',
          life: 0,
          maxLife: 130 + Math.random() * 70,
        });
      }

      this.canvas.classList.add('is-active');
      if (!this.rafId) this.animate();
    }

    /** Advances and draws one animation frame, looping via requestAnimationFrame. */
    animate() {
      if (!this.ctx || !this.canvas) return;

      const width = this.canvas.clientWidth;
      const height = this.canvas.clientHeight;
      this.ctx.clearRect(0, 0, width, height);

      this.particles.forEach((particle) => {
        particle.x += particle.speedX;
        particle.y += particle.speedY;
        particle.speedY += 0.02;
        particle.rotation += particle.rotationSpeed;
        particle.life += 1;

        this.ctx.save();
        this.ctx.translate(particle.x, particle.y);
        this.ctx.rotate((particle.rotation * Math.PI) / 180);
        this.ctx.globalAlpha = Math.max(0, 1 - particle.life / particle.maxLife);
        this.ctx.fillStyle = particle.color;

        if (particle.shape === 'rect') {
          this.ctx.fillRect(-particle.size / 2, -particle.size / 4, particle.size, particle.size / 2);
        } else {
          this.ctx.beginPath();
          this.ctx.arc(0, 0, particle.size / 2, 0, Math.PI * 2);
          this.ctx.fill();
        }

        this.ctx.restore();
      });

      this.particles = this.particles.filter((particle) => particle.life < particle.maxLife && particle.y < height + 40);

      if (this.particles.length > 0) {
        this.rafId = window.requestAnimationFrame(() => this.animate());
      } else {
        this.rafId = null;
        this.canvas.classList.remove('is-active');
      }
    }
  }

  /**
   * BundleState
   * Single source of truth for the two sets being built. Owns product/print
   * lookups, completion and validation logic, and derived labels. Never
   * touches the DOM, sessionStorage, or the network.
   */
  class BundleState {
    constructor({ config, prints }) {
      this.config = config;
      this.prints = Array.isArray(prints) ? prints : [];
      this.sets = [this.emptySet(), this.emptySet()];
      this.activeSetIndex = 0;
      this.printPickerExpanded = true;
      this.isAdding = false;
    }

    /** Returns a blank set shape. */
    emptySet() {
      return {
        print: '',
        sleeve: '',
        bottom: '',
        topSize: '',
        bottomSize: '',
      };
    }

    /** The set currently being edited. */
    get activeSet() {
      return this.sets[this.activeSetIndex];
    }

    /**
     * Replaces the current sets with a previously-saved pair, discarding any
     * saved size that no longer resolves to an available variant.
     */
    hydrate(savedSets) {
      if (!Array.isArray(savedSets) || savedSets.length !== 2) return;

      this.sets = savedSets.map((set) => ({
        ...this.emptySet(),
        ...set,
      }));

      this.sets.forEach((set, index) => {
        const variants = this.setVariants(set);
        if (set.topSize && !variants.top) this.sets[index].topSize = '';
        if (set.bottomSize && !variants.bottom) this.sets[index].bottomSize = '';
      });
    }

    /** Clears the currently active set back to its blank shape. */
    resetActiveSet() {
      this.sets[this.activeSetIndex] = this.emptySet();
    }

    /** Clears both sets and returns focus to set 1. Used after a successful add-to-cart. */
    resetAllSets() {
      this.sets = [this.emptySet(), this.emptySet()];
      this.activeSetIndex = 0;
    }

    /** Merges partial updates into the active set. */
    updateActiveSet(updates) {
      Object.assign(this.activeSet, updates);
    }

    /** Switches the active set, syncing whether the print picker should be expanded. Returns false for an out-of-range index. */
    setActiveIndex(index) {
      if (!Number.isInteger(index) || index < 0 || index > 1) return false;
      this.activeSetIndex = index;
      this.printPickerExpanded = !this.sets[index].print;
      return true;
    }

    printConfig(printName) {
      return this.prints.find((print) => BundleUtils.normalise(print?.name) === BundleUtils.normalise(printName)) || null;
    }

    productFor(productKey, printName) {
      return this.printConfig(printName)?.products?.[productKey] || null;
    }

    variantSize(variant) {
      return String(variant?.options?.[0] || variant?.title || '').trim();
    }

    availableSizes(productKey, printName) {
      const product = this.productFor(productKey, printName);
      if (!product) return [];

      const values = product.variants
        .filter((variant) => variant.available)
        .map((variant) => this.variantSize(variant))
        .filter(Boolean);

      return [...new Set(values)];
    }

    findVariant(productKey, printName, size) {
      const product = this.productFor(productKey, printName);
      if (!product || !size) return null;

      return product.variants.find((variant) => {
        return variant.available && BundleUtils.normalise(this.variantSize(variant)) === BundleUtils.normalise(size);
      }) || null;
    }

    setVariants(set) {
      if (!set?.sleeve || !set?.bottom) return { top: null, bottom: null };
      return {
        top: this.findVariant(set.sleeve, set.print, set.topSize),
        bottom: this.findVariant(set.bottom, set.print, set.bottomSize),
      };
    }

    isSetComplete(set) {
      const variants = this.setVariants(set);
      return Boolean(variants.top && variants.bottom);
    }

    completedCount() {
      return this.sets.filter((set) => this.isSetComplete(set)).length;
    }

    firstIncompleteSetIndex() {
      const index = this.sets.findIndex((set) => !this.isSetComplete(set));
      return index >= 0 ? index : 1;
    }

    hasStarted(set) {
      return Object.values(set).some(Boolean);
    }

    printHasAnyCompleteCombination(print) {
      const topAvailable = ['half', 'full'].some((key) => this.availableSizes(key, print).length > 0);
      const bottomAvailable = ['shorts', 'pyjamas'].some((key) => this.availableSizes(key, print).length > 0);
      return topAvailable && bottomAvailable;
    }

    /** Short "what to do next" copy for the active set. */
    guidanceFor(set) {
      if (!set.print) return 'choose a print to reveal the top and bottom options.';
      if (!set.sleeve && !set.bottom) return 'now choose your top and bottom styles.';
      if (!set.sleeve) return 'choose a top style.';
      if (!set.topSize) return 'choose a size for your top.';
      if (!set.bottom) return 'choose a bottom style.';
      if (!set.bottomSize) return 'choose a size for your bottom.';
      return `set ${this.activeSetIndex + 1} is ready to save.`;
    }

    incompleteSummary(set) {
      const values = [];
      if (set.print) values.push(set.print);
      if (set.sleeve) values.push(this.sleeveLabel(set.sleeve));
      if (set.bottom) values.push(this.bottomLabel(set.bottom));
      return values.length ? values.join(' · ') : 'continue building this set';
    }

    sleeveLabel(value) {
      return value === 'full' ? 'full sleeve top' : value === 'half' ? 'half sleeve top' : '';
    }

    bottomLabel(value) {
      return value === 'pyjamas' ? 'pyjamas' : value === 'shorts' ? 'shorts' : '';
    }

    /** Human-readable description of a set, used as a cart line-item property. */
    publicSetDescription(set) {
      return `${set.print} / ${this.sleeveLabel(set.sleeve)} ${set.topSize} / ${this.bottomLabel(set.bottom)} ${set.bottomSize}`;
    }

    productChoiceImage(productKey, printName) {
      const print = this.printConfig(printName);
      if (!print) return '';

      const overrideImage = print.images?.[productKey];
      if (overrideImage) return overrideImage;

      const product = print.products?.[productKey];
      const variant = product?.variants?.find((item) => item.available && item.image)
        || product?.variants?.find((item) => item.image);

      return variant?.image || '';
    }

    representativeImage(printName) {
      for (const key of ['half', 'full', 'shorts', 'pyjamas']) {
        const product = this.productFor(key, printName);
        const variant = product?.variants?.find((item) => item.available && item.image)
          || product?.variants?.find((item) => item.image);
        if (variant?.image) return variant.image;
      }
      return '';
    }

    /**
     * Validates the active set. Returns a plain result object rather than
     * touching the DOM directly, so BundleRenderer decides how to display it.
     */
    validateActiveSet() {
      const set = this.activeSet;
      const errors = { print: '', topSize: '', bottomSize: '', status: '' };
      let valid = true;

      if (!set.print) {
        errors.print = 'choose a print.';
        valid = false;
      }

      if (!set.sleeve) {
        errors.status = 'choose half sleeve or full sleeve.';
        valid = false;
      } else if (!set.topSize || !this.findVariant(set.sleeve, set.print, set.topSize)) {
        errors.topSize = 'choose an available top size.';
        valid = false;
      }

      if (!set.bottom) {
        errors.status = 'choose shorts or pyjamas.';
        valid = false;
      } else if (!set.bottomSize || !this.findVariant(set.bottom, set.print, set.bottomSize)) {
        errors.bottomSize = 'choose an available bottom size.';
        valid = false;
      }

      return { valid, errors };
    }
  }

  /**
   * BundleStorage
   * Thin sessionStorage wrapper. Knows nothing about rendering, cart logic,
   * or Shopify — it only persists and returns plain set arrays.
   */
  class BundleStorage {
    constructor(storageKey) {
      this.storageKey = storageKey;
    }

    /** Persists the current sets. Silently no-ops if storage is unavailable. */
    save(sets) {
      try {
        sessionStorage.setItem(this.storageKey, JSON.stringify(sets));
      } catch (_) {
        // The builder still works when storage is unavailable.
      }
    }

    /** Returns the saved sets array, or null if nothing valid was found. */
    restore() {
      try {
        const saved = JSON.parse(sessionStorage.getItem(this.storageKey) || 'null');
        return Array.isArray(saved) ? saved : null;
      } catch (_) {
        this.clear();
        return null;
      }
    }

    /** Removes any saved state. Silently no-ops if storage is unavailable. */
    clear() {
      try {
        sessionStorage.removeItem(this.storageKey);
      } catch (_) {
        // Ignore storage failures.
      }
    }
  }

  /**
   * BundleCart
   * Owns all Shopify cart communication: building line-item payloads,
   * posting to cart/add.js, refreshing the cart, and firing cart events.
   * It knows nothing about rendering — callers observe progress and
   * outcomes purely through the callbacks supplied at construction time.
   */
  class BundleCart {
    constructor({ config, onStatusChange, onLoadingChange, onSuccess }) {
      this.config = config;
      this.onStatusChange = typeof onStatusChange === 'function' ? onStatusChange : () => {};
      this.onLoadingChange = typeof onLoadingChange === 'function' ? onLoadingChange : () => {};
      this.onSuccess = typeof onSuccess === 'function' ? onSuccess : () => {};
    }

    /** Builds one top + one bottom cart line per set, tagged with shared bundle properties. */
    buildComponentItems(sets, bundleId, state) {
      return sets.flatMap((set, index) => {
        const variants = state.setVariants(set);
        const common = {
          _bundle_id: bundleId,
          _bundle_offer_id: this.config.offerId,
          _bundle_set: String(index + 1),
          _bundle_print: set.print,
          _bundle_top_style: set.sleeve,
          _bundle_bottom_type: set.bottom,
        };

        return [
          {
            id: variants.top.id,
            quantity: 1,
            properties: { ...common, _bundle_role: 'top' },
          },
          {
            id: variants.bottom.id,
            quantity: 1,
            properties: { ...common, _bundle_role: 'bottom' },
          },
        ];
      });
    }

    /** Builds a single fixed-price bundle product line, describing both sets in its properties. */
    buildFixedProductItem(sets, bundleId, state) {
      const variantId = Number(this.config.fixedBundleVariantId);
      if (!Number.isSafeInteger(variantId)) {
        throw new Error('the fixed-price bundle product is not configured.');
      }

      const properties = {
        'Set 1': state.publicSetDescription(sets[0]),
        'Set 2': state.publicSetDescription(sets[1]),
        _bundle_id: bundleId,
        _bundle_offer_id: this.config.offerId,
      };

      return [{ id: variantId, quantity: 1, properties }];
    }

    buildItems(sets, bundleId, state) {
      return this.config.cartMode === 'fixed_product'
        ? this.buildFixedProductItem(sets, bundleId, state)
        : this.buildComponentItems(sets, bundleId, state);
    }

    /**
     * Adds both sets to the cart as a single transaction. Resolves to
     * `{ success, redirected? }`; status text and loading state are reported
     * via the constructor callbacks, matching the original messaging exactly.
     */
    async addToCart(sets, state) {
      const bundleId = BundleUtils.createBundleId();
      let items;

      try {
        items = this.buildItems(sets, bundleId, state);
      } catch (error) {
        this.onStatusChange(error.message);
        return { success: false };
      }

      this.onLoadingChange(true);
      this.onStatusChange('adding both sets to your cart.');

      try {
        const rootPath = window.Shopify?.routes?.root || '/';
        const sections = new Set();

        document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections },
        }));

        const response = await fetch(`${rootPath}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            items,
            sections: [...sections].join(','),
          }),
        });

        const payload = await response.json();
        if (!response.ok) {
          throw new Error(payload.description || payload.message || 'unable to add the bundle.');
        }

        this.onStatusChange('both sets were added to your cart.');
        document.dispatchEvent(new CustomEvent('bundle:added', {
          detail: { bundleId, items: payload.items || payload },
        }));

        if (window.themeVariables?.settings?.cartType === 'page' ||
            window.themeVariables?.settings?.pageType === 'cart') {
          window.location.assign(`${rootPath}cart`);
          return { success: true, redirected: true };
        }

        const cartResponse = await fetch(`${rootPath}cart.js`, {
          headers: { Accept: 'application/json' },
        });

        if (!cartResponse.ok) {
          throw new Error('the bundle was added, but the cart could not be refreshed.');
        }

        const cart = await cartResponse.json();
        cart.sections = payload.sections || {};

        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'variant:add', cart },
        }));

        this.onSuccess();
        return { success: true };
      } catch (error) {
        this.onStatusChange(error.message || 'we could not add the bundle. please try again.');
        return { success: false };
      } finally {
        this.onLoadingChange(false);
      }
    }
  }

  /**
   * BundleRenderer
   * Owns every UI update. Reads a BundleState snapshot and writes it into
   * cached DOM references. Never performs fetches and never changes
   * business logic — it only converts state into markup/attributes.
   */
  class BundleRenderer {
    constructor({ root, moneyFormat }) {
      this.root = root;
      this.moneyFormat = moneyFormat;
      this.cacheDom();
    }

    /** Caches the DOM nodes/lists that are read or written on every render pass. */
    cacheDom() {
      const root = this.root;
      this.dom = {
        editor: root.querySelector('[data-set-editor]'),
        reviewSheet: root.querySelector('[data-review-sheet]'),
        globalStatus: root.querySelector('[data-global-status]'),
        editorStatus: root.querySelector('[data-editor-status]'),
        addButtons: [...root.querySelectorAll('[data-add-bundle], [data-mobile-add-bundle], [data-sheet-add-bundle]')],
        setTabs: [...root.querySelectorAll('[data-set-tab]')],
        printChoices: [...root.querySelectorAll('[data-print-choice]')],
        sleeveChoices: [...root.querySelectorAll('[data-sleeve-choice]')],
        bottomChoices: [...root.querySelectorAll('[data-bottom-choice]')],
        activeSetLabel: root.querySelector('[data-active-set-label]'),
        printPicker: root.querySelector('[data-print-picker]'),
        selectedPrint: root.querySelector('[data-selected-print]'),
        selectedPrintName: root.querySelector('[data-selected-print-name]'),
        printStage: root.querySelector('[data-print-stage]'),
        productStage: root.querySelector('[data-product-stage]'),
        topSizeStage: root.querySelector('[data-top-size-stage]'),
        bottomSizeStage: root.querySelector('[data-bottom-size-stage]'),
        topSizeOptions: root.querySelector('[data-top-size-options]'),
        bottomSizeOptions: root.querySelector('[data-bottom-size-options]'),
        topSizeContext: root.querySelector('[data-top-size-context]'),
        bottomSizeContext: root.querySelector('[data-bottom-size-context]'),
        resetActiveSet: root.querySelector('[data-reset-active-set]'),
        confirmSet: root.querySelector('[data-confirm-set]'),
        stepGuidance: root.querySelector('[data-step-guidance]'),
        overallProgress: root.querySelector('[data-overall-progress]'),
        overallInstruction: root.querySelector('[data-overall-instruction]'),
        overallProgressbar: root.querySelector('[data-overall-progressbar]'),
        overallProgressFill: root.querySelector('[data-overall-progress-fill]'),
        mobileProgress: root.querySelector('[data-mobile-progress]'),
        regularTotal: root.querySelector('[data-regular-total]'),
        savings: root.querySelector('[data-savings]'),
        summaryHeading: root.querySelector('[data-summary-heading]'),
        mobileSummary: root.querySelector('[data-mobile-summary]'),
      };
    }

    /** Full render pass: tabs, editor, summary, progress, and add buttons. */
    render(state) {
      this.renderTabs(state);
      this.renderEditor(state);
      this.renderSummary(state);
      this.renderProgress(state);
      this.renderAddButtons(state);
    }

    renderTabs(state) {
      this.dom.setTabs.forEach((button) => {
        const index = Number(button.dataset.setTab);
        const active = index === state.activeSetIndex;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });

      state.sets.forEach((set, index) => {
        const status = this.root.querySelector(`[data-set-tab-status="${index}"]`);
        if (!status) return;

        if (state.isSetComplete(set)) {
          status.textContent = 'complete';
        } else if (state.hasStarted(set)) {
          status.textContent = 'in progress';
        } else {
          status.textContent = 'not started';
        }
      });
    }

    renderEditor(state) {
      const set = state.activeSet;
      if (this.dom.activeSetLabel) this.dom.activeSetLabel.textContent = `set ${state.activeSetIndex + 1}`;

      this.dom.printChoices.forEach((button) => {
        const selected = BundleUtils.normalise(button.dataset.printChoice) === BundleUtils.normalise(set.print);
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
        button.disabled = !state.printHasAnyCompleteCombination(button.dataset.printChoice);
      });

      const showPrintPicker = !set.print || state.printPickerExpanded;

      if (this.dom.printPicker) this.dom.printPicker.hidden = !showPrintPicker;
      if (this.dom.selectedPrint) this.dom.selectedPrint.hidden = !set.print || showPrintPicker;
      if (this.dom.selectedPrintName) this.dom.selectedPrintName.textContent = set.print;
      if (this.dom.printStage) this.dom.printStage.classList.toggle('is-collapsed', Boolean(set.print) && !showPrintPicker);

      if (this.dom.productStage) this.dom.productStage.hidden = !set.print;
      this.renderProductChoiceImages(state, set.print);

      this.dom.sleeveChoices.forEach((button) => {
        const key = button.dataset.sleeveChoice;
        const selected = key === set.sleeve;
        const unavailable = Boolean(set.print) && state.availableSizes(key, set.print).length === 0;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
        button.disabled = !set.print || unavailable;
      });

      this.dom.bottomChoices.forEach((button) => {
        const key = button.dataset.bottomChoice;
        const selected = key === set.bottom;
        const unavailable = Boolean(set.print) && state.availableSizes(key, set.print).length === 0;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
        button.disabled = !set.print || unavailable;
      });

      if (this.dom.topSizeStage) this.dom.topSizeStage.hidden = !set.sleeve;
      if (this.dom.bottomSizeStage) this.dom.bottomSizeStage.hidden = !set.bottom;

      this.renderSizeOptions(state, 'topSize', set.sleeve, set.print, set.topSize, this.dom.topSizeOptions);
      this.renderSizeOptions(state, 'bottomSize', set.bottom, set.print, set.bottomSize, this.dom.bottomSizeOptions);

      if (this.dom.topSizeContext) {
        this.dom.topSizeContext.textContent = set.sleeve && set.print
          ? `${state.productFor(set.sleeve, set.print)?.title || 'top'} · ${set.print}`
          : '';
      }

      if (this.dom.bottomSizeContext) {
        this.dom.bottomSizeContext.textContent = set.bottom && set.print
          ? `${state.productFor(set.bottom, set.print)?.title || 'bottom'} · ${set.print}`
          : '';
      }

      if (this.dom.resetActiveSet) this.dom.resetActiveSet.disabled = !state.hasStarted(set);

      const complete = state.isSetComplete(set);
      if (this.dom.confirmSet) {
        this.dom.confirmSet.hidden = !set.print;
        this.dom.confirmSet.disabled = !complete;

        if (state.activeSetIndex === 0 && !state.isSetComplete(state.sets[1])) {
          this.dom.confirmSet.textContent = 'save set 1 and build set 2';
        } else {
          this.dom.confirmSet.textContent = state.completedCount() === 2 ? 'review bundle' : `save set ${state.activeSetIndex + 1}`;
        }
      }

      if (this.dom.stepGuidance) this.dom.stepGuidance.textContent = state.guidanceFor(set);
    }

    renderSizeOptions(state, field, productKey, print, selectedSize, container) {
      if (!container) return;

      if (!productKey || !print) {
        container.innerHTML = '<span class="bundle-size-options__empty">Make the choices above first.</span>';
        return;
      }

      const sizes = state.availableSizes(productKey, print);
      if (sizes.length === 0) {
        container.innerHTML = '<span class="bundle-size-options__empty">No sizes available for this combination.</span>';
        return;
      }

      container.innerHTML = sizes.map((size) => {
        const selected = BundleUtils.normalise(size) === BundleUtils.normalise(selectedSize);
        return `
          <button
            type="button"
            class="bundle-size-chip${selected ? ' is-selected' : ''}"
            data-size-choice="${BundleUtils.escapeHtml(size)}"
            data-size-field="${field}"
            aria-pressed="${selected}"
          >${BundleUtils.escapeHtml(size)}</button>`;
      }).join('');
    }

    renderProgress(state) {
      const count = state.completedCount();

      if (this.dom.overallProgress) this.dom.overallProgress.textContent = `${count} of 2 sets complete`;
      if (this.dom.mobileProgress) this.dom.mobileProgress.textContent = `${count} of 2 sets`;
      if (this.dom.overallProgressbar) this.dom.overallProgressbar.setAttribute('aria-valuenow', String(count));
      if (this.dom.overallProgressFill) this.dom.overallProgressFill.style.width = `${count * 50}%`;

      if (this.dom.overallInstruction) {
        this.dom.overallInstruction.textContent = count === 2
          ? 'your bundle is ready'
          : `complete set ${state.firstIncompleteSetIndex() + 1}`;
      }
    }

    renderSummary(state) {
      let regularTotalMinor = 0;

      state.sets.forEach((set, index) => {
        const card = this.root.querySelector(`[data-summary-set="${index}"]`);
        if (!card) return;

        const content = card.querySelector('.bundle-summary-set__content');
        const variants = state.setVariants(set);
        const complete = Boolean(variants.top && variants.bottom);
        card.classList.toggle('is-complete', complete);

        if (complete) {
          regularTotalMinor += Number(variants.top.price || 0) + Number(variants.bottom.price || 0);
          content.innerHTML = `
            <strong>${BundleUtils.escapeHtml(set.print)}</strong>
            <span>${BundleUtils.escapeHtml(state.sleeveLabel(set.sleeve))} · ${BundleUtils.escapeHtml(set.topSize)}</span>
            <span>${BundleUtils.escapeHtml(state.bottomLabel(set.bottom))} · ${BundleUtils.escapeHtml(set.bottomSize)}</span>`;
        } else if (state.hasStarted(set)) {
          content.innerHTML = `
            <strong>Set ${index + 1} · In progress</strong>
            <span>${BundleUtils.escapeHtml(state.incompleteSummary(set))}</span>`;
        } else {
          content.innerHTML = `
            <strong>Set ${index + 1}</strong>
            <span>Choose a print, top and bottom</span>`;
        }
      });

      const complete = state.completedCount() === 2;
      const savingsMinor = complete
        ? Math.max(0, regularTotalMinor - Number(state.config.bundlePriceMinor || 0))
        : 0;

      if (this.dom.regularTotal) this.dom.regularTotal.textContent = BundleUtils.formatMoney(regularTotalMinor, this.moneyFormat);
      if (this.dom.savings) this.dom.savings.textContent = BundleUtils.formatMoney(savingsMinor, this.moneyFormat);
      if (this.dom.summaryHeading) {
        this.dom.summaryHeading.textContent = complete
          ? 'bundle complete'
          : `choose ${2 - state.completedCount()} more set${2 - state.completedCount() === 1 ? '' : 's'}`;
      }

      this.renderMobileSummary(state);
    }

    renderMobileSummary(state) {
      if (!this.dom.mobileSummary) return;

      this.dom.mobileSummary.innerHTML = state.sets.map((set, index) => {
        const complete = state.isSetComplete(set);
        const copy = complete
          ? `${BundleUtils.escapeHtml(set.print)}<br><span>${BundleUtils.escapeHtml(state.sleeveLabel(set.sleeve))} · ${BundleUtils.escapeHtml(set.topSize)}</span><br><span>${BundleUtils.escapeHtml(state.bottomLabel(set.bottom))} · ${BundleUtils.escapeHtml(set.bottomSize)}</span>`
          : '<span>not complete</span>';

        return `
          <article class="bundle-review-set${complete ? ' is-complete' : ''}">
            <div>
              <small>SET ${index + 1}</small>
              <strong>${copy}</strong>
            </div>
            <button type="button" data-edit-set="${index}">Edit</button>
          </article>`;
      }).join('');
    }

    renderAddButtons(state) {
      const complete = state.completedCount() === 2;
      this.dom.addButtons.forEach((button) => {
        button.disabled = !complete || state.isAdding;
        button.classList.toggle('is-loading', state.isAdding);

        if (button.matches('[data-mobile-add-bundle]')) {
          button.textContent = state.isAdding ? 'adding…' : 'add';
        } else {
          button.textContent = state.isAdding ? 'adding your bundle…' : state.config.addButtonLabel;
        }
      });
    }

    renderProductChoiceImages(state, printName) {
      ['half', 'full', 'shorts', 'pyjamas'].forEach((productKey) => {
        const image = this.root.querySelector(`[data-product-choice-image="${productKey}"]`);
        const placeholder = this.root.querySelector(`[data-product-choice-placeholder="${productKey}"]`);
        if (!image || !placeholder) return;

        const source = printName ? state.productChoiceImage(productKey, printName) : '';
        const label = productKey === 'half'
          ? 'half sleeve'
          : productKey === 'full'
            ? 'full sleeve'
            : productKey;

        if (source) {
          image.src = source;
          image.alt = `${printName} ${label}`;
          image.hidden = false;
          placeholder.hidden = true;
        } else {
          image.removeAttribute('src');
          image.alt = '';
          image.hidden = true;
          placeholder.hidden = false;
        }
      });
    }

    /** Sets each print swatch's background image to a representative product photo. */
    applyVariantImages(state) {
      this.dom.printChoices.forEach((button) => {
        const placeholder = button.querySelector('[data-print-image-placeholder]');
        if (!placeholder) return;

        const print = button.dataset.printChoice;
        const image = state.representativeImage(print);
        if (image) placeholder.style.backgroundImage = `url("${image.replaceAll('"', '%22')}")`;
      });
    }

    showError(selector, message) {
      const element = this.root.querySelector(selector);
      if (!element) return;
      element.textContent = message;
      element.hidden = false;
    }

    clearErrors() {
      this.root.querySelectorAll('.bundle-field-error').forEach((element) => {
        element.textContent = '';
        element.hidden = true;
      });
      this.setEditorStatus('');
    }

    /** Displays a BundleState#validateActiveSet() result and scrolls to the first visible error. */
    applyValidationErrors({ valid, errors }) {
      this.clearErrors();

      if (errors.print) this.showError('[data-print-error]', errors.print);
      if (errors.topSize) this.showError('[data-top-size-error]', errors.topSize);
      if (errors.bottomSize) this.showError('[data-bottom-size-error]', errors.bottomSize);
      if (errors.status) this.setEditorStatus(errors.status);

      if (!valid) {
        const firstError = this.root.querySelector('.bundle-field-error:not([hidden])');
        firstError?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    setEditorStatus(message) {
      if (this.dom.editorStatus) this.dom.editorStatus.textContent = message;
    }

    setGlobalStatus(message) {
      if (this.dom.globalStatus) this.dom.globalStatus.textContent = message;
    }

    /** Scrolls a mobile stage into view once it has rendered, ignoring the request on desktop. */
    focusStage(selector) {
      window.requestAnimationFrame(() => {
        const stage = this.root.querySelector(selector);
        if (!stage || stage.hidden) return;

        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const mobile = window.matchMedia('(max-width: 749px)').matches;
        if (!mobile) return;

        stage.scrollIntoView({
          behavior: reduceMotion ? 'auto' : 'smooth',
          block: 'nearest',
        });
      });
    }

    scrollEditorIntoView() {
      this.dom.editor?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    openReview() {
      if (!this.dom.reviewSheet) return;
      if (typeof this.dom.reviewSheet.showModal === 'function') {
        this.dom.reviewSheet.showModal();
      } else {
        this.dom.reviewSheet.setAttribute('open', '');
      }
    }

    closeReview() {
      if (!this.dom.reviewSheet?.open) return;
      if (typeof this.dom.reviewSheet.close === 'function') {
        this.dom.reviewSheet.close();
      } else {
        this.dom.reviewSheet.removeAttribute('open');
      }
    }
  }

  /**
   * BundleSetBuilder
   * Lightweight controller. Wires BundleState, BundleStorage, BundleCart, and
   * BundleRenderer together, binds DOM events, and translates each user
   * interaction into state changes followed by a render. Contains almost no
   * business logic of its own.
   */
  class BundleSetBuilder {
    constructor(root) {
      this.root = root;
      const data = this.readData();
      if (!data) return;

      this.state = new BundleState({ config: data.config, prints: data.prints });

      const moneyFormat = root.dataset.moneyFormat || '₹{{amount}}';
      const storageKey = `two-set-builder:${this.state.config.offerId || root.dataset.sectionId}`;

      this.storage = new BundleStorage(storageKey);
      this.renderer = new BundleRenderer({ root, moneyFormat });
      this.confetti = new SectionConfetti(root.querySelector('[data-confetti-canvas]'));
      this.cart = new BundleCart({
        config: this.state.config,
        onStatusChange: (message) => this.renderer.setGlobalStatus(message),
        onLoadingChange: (isLoading) => {
          this.state.isAdding = isLoading;
          this.renderer.renderAddButtons(this.state);
        },
        onSuccess: () => {
          this.state.resetAllSets();
          this.storage.save(this.state.sets);
          this.renderer.closeReview();
          this.renderer.render(this.state);
        },
      });

      this.restore();
      this.state.activeSetIndex = this.state.firstIncompleteSetIndex();
      this.state.printPickerExpanded = !this.state.activeSet.print;

      this.bindEvents();
      this.renderer.applyVariantImages(this.state);
      this.renderer.render(this.state);
    }

    readData() {
      const script = this.root.querySelector('[data-set-builder-data]');
      if (!script) return null;

      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        console.error('Bundle builder configuration is invalid.', error);
        return null;
      }
    }

    restore() {
      const saved = this.storage.restore();
      this.state.hydrate(saved);
    }

    bindEvents() {
      this.root.addEventListener('click', (event) => {
        const setTab = event.target.closest('[data-set-tab]');
        if (setTab) {
          this.activateSet(Number(setTab.dataset.setTab));
          return;
        }

        const printChoice = event.target.closest('[data-print-choice]');
        if (printChoice && !printChoice.disabled) {
          this.state.printPickerExpanded = false;
          this.updateActiveSet({
            print: printChoice.dataset.printChoice,
            topSize: '',
            bottomSize: '',
          });
          this.renderer.focusStage('[data-product-stage]');
          return;
        }

        const changePrint = event.target.closest('[data-change-print]');
        if (changePrint) {
          this.state.printPickerExpanded = true;
          this.renderer.clearErrors();
          this.renderer.render(this.state);
          this.renderer.focusStage('[data-print-stage]');
          return;
        }

        const sleeveChoice = event.target.closest('[data-sleeve-choice]');
        if (sleeveChoice && !sleeveChoice.disabled) {
          this.updateActiveSet({
            sleeve: sleeveChoice.dataset.sleeveChoice,
            topSize: '',
          });
          this.renderer.focusStage('[data-top-size-stage]');
          return;
        }

        const bottomChoice = event.target.closest('[data-bottom-choice]');
        if (bottomChoice && !bottomChoice.disabled) {
          this.updateActiveSet({
            bottom: bottomChoice.dataset.bottomChoice,
            bottomSize: '',
          });
          this.renderer.focusStage('[data-bottom-size-stage]');
          return;
        }

        const sizeChoice = event.target.closest('[data-size-choice]');
        if (sizeChoice && !sizeChoice.disabled) {
          const field = sizeChoice.dataset.sizeField;
          this.updateActiveSet({ [field]: sizeChoice.dataset.sizeChoice });
          return;
        }

        const reset = event.target.closest('[data-reset-active-set]');
        if (reset) {
          this.state.resetActiveSet();
          this.state.printPickerExpanded = true;
          this.storage.save(this.state.sets);
          this.renderer.clearErrors();
          this.renderer.render(this.state);
          return;
        }

        const confirm = event.target.closest('[data-confirm-set]');
        if (confirm) {
          this.confirmActiveSet();
          return;
        }

        const edit = event.target.closest('[data-edit-set]');
        if (edit) {
          this.renderer.closeReview();
          this.activateSet(Number(edit.dataset.editSet), true);
          return;
        }

        const review = event.target.closest('[data-mobile-review]');
        if (review) {
          this.renderer.openReview();
          return;
        }

        const reviewClose = event.target.closest('[data-review-close]');
        if (reviewClose) {
          this.renderer.closeReview();
          return;
        }

        const add = event.target.closest('[data-add-bundle], [data-mobile-add-bundle], [data-sheet-add-bundle]');
        if (add) this.addToCart();
      });

      this.renderer.dom.reviewSheet?.addEventListener('click', (event) => {
        if (event.target === this.renderer.dom.reviewSheet) this.renderer.closeReview();
      });
    }

    activateSet(index, scroll = false) {
      if (!this.state.setActiveIndex(index)) return;
      this.renderer.clearErrors();
      this.renderer.render(this.state);

      if (scroll) this.renderer.scrollEditorIntoView();
    }

    updateActiveSet(updates) {
      this.state.updateActiveSet(updates);
      this.storage.save(this.state.sets);
      this.renderer.clearErrors();
      this.renderer.render(this.state);
    }

    confirmActiveSet() {
      const validation = this.state.validateActiveSet();
      this.renderer.applyValidationErrors(validation);
      if (!validation.valid) return;

      if (this.state.activeSetIndex === 0 && !this.state.isSetComplete(this.state.sets[1])) {
        this.state.setActiveIndex(1);
        this.renderer.setEditorStatus('set 1 saved. now build set 2.');
        this.storage.save(this.state.sets);
        this.renderer.render(this.state);
        this.confetti?.burst({ count: 110 });
        this.renderer.scrollEditorIntoView();
        return;
      }

      this.storage.save(this.state.sets);
      this.renderer.render(this.state);
      this.confetti?.burst({ count: 170 });
      this.renderer.openReview();
    }

    async addToCart() {
      if (this.state.completedCount() !== 2 || this.state.isAdding) return;
      await this.cart.addToCart(this.state.sets, this.state);
    }
  }

  function init(scope = document) {
    scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (root.dataset.setBuilderInitialized === 'true') return;
      root.dataset.setBuilderInitialized = 'true';
      new BundleSetBuilder(root);
    });
  }

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();